import type { AppProps } from "next/app";
import { LanguageProvider } from "../hooks/useLanguage";
import IntlProvider from "../components/providers/IntlProvider";
import React, { ReactNode, StrictMode } from "react";
import useServiceWorkerEvents from "../hooks/useServiceWorkerEvents";
import BannerOrchestrator from "../contexts/BannerOrchestrator";
import Bugsnag from "@bugsnag/js";
import { isInjectedScriptError } from "../config/bugsnag";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { SWRConfig } from "swr";
import styled, { StyleSheetManager } from "styled-components";
import swrConfig from "../config/swr";
import {
  registerCustomTranslationMessages,
  registerCustomYupValidationRules,
} from "../services/validationService";
import "/public/global.css";
import "react-loading-skeleton/dist/skeleton.css";
import { Amplify } from "@aws-amplify/core";
import awsConfig from "../../aws-config";
import { AuthProvider } from "../contexts/Auth/Auth";
import NextNProgress from "nextjs-progressbar";
import { ModalProvider } from "../components/atoms/Modal/modal.provider";
import wrapper from "../redux";
import { HeadsupProvider } from "../components/atoms/Headsup/Headsup.provider";
import { ThemeProvider, useTheme } from "../hooks/useTheme";
import { LabelProvider, useLabel } from "../hooks/useLabel";
import { HeadLabel } from "../components/atoms/head";
import { MaintenanceMode } from "../components/atoms/MaintenanceMode";
import { CookiesProvider } from "react-cookie";
import isPropValid from "@emotion/is-prop-valid";

Amplify.configure(awsConfig);

// Third-party / browser noise we never want to report to BugSnag. Pairing the
// error class with the message keeps generic messages from masking real bugs.
const DISCARDED_ERRORS: { errorClass?: string; message: string }[] = [
  { message: "ResizeObserver loop" },
  { errorClass: "SecurityError", message: "The operation is insecure." },
  { errorClass: "ReferenceError", message: "SCDynimacBridge" },
  {
    errorClass: "NotFoundError",
    message: "Failed to execute 'removeChild' on 'Node'",
  },
  { errorClass: "NotFoundError", message: "The object can not be found here." },
  { message: "attempted to hard navigate to the same URL" },
  { message: "Cancel rendering route" },
  { errorClass: "AbortError", message: "signal is aborted without reason" },
  {
    errorClass: "TypeError",
    message: "is not an object (evaluating 'e.getAttribute')",
  },
];

let ErrorBoundary: any = styled.div``;

try {
  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY as string,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NEXT_PUBLIC_BUGSNAG_STAGE as string,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    onError: (event) => {
      if (typeof document !== "undefined") {
        event.addMetadata("page", {
          scripts: Array.from(document.scripts).map(
            (script) =>
              script.src || `inline:${script.textContent?.length ?? 0}`
          ),
          userAgent: navigator.userAgent,
        });
      }

      return !event.errors?.some(
        (error) =>
          isInjectedScriptError(error) ||
          DISCARDED_ERRORS.some(
            ({ errorClass, message }) =>
              (!errorClass || error.errorClass === errorClass) &&
              (error.errorMessage ?? "").includes(message)
          )
      );
    },
  });

  ErrorBoundary =
    Bugsnag.getPlugin("react")?.createErrorBoundary(React) ?? styled.div``;
} catch (e) {
  //
}

registerCustomYupValidationRules();
registerCustomTranslationMessages();

// Wrap NextNProgress in wrapper so we can use ThemeProvider
const ProgressWrapper = () => {
  const { theme } = useTheme();
  return (
    <NextNProgress
      height={4}
      color={theme.colors.PRIMARY}
      showOnShallow={false}
      options={{ showSpinner: false }}
    />
  );
};

const ThemeProviderWrapper = ({ children }: { children: ReactNode }) => {
  const {
    label: { theme },
  } = useLabel();
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

function MyApp({ Component, pageProps }: AppProps) {
  useServiceWorkerEvents();

  const initialLabel: string | undefined = pageProps?.__label ?? undefined;

  return (
    <ErrorBoundary>
      <CookiesProvider>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          <SWRConfig value={swrConfig}>
            <AuthProvider>
              <LabelProvider initialLabel={initialLabel}>
                <ThemeProviderWrapper>
                  <StrictMode>
                    <LanguageProvider>
                      <IntlProvider>
                        <HeadsupProvider>
                          <ModalProvider>
                            <BannerOrchestrator>
                              <MaintenanceMode />
                              <ProgressWrapper />
                              <HeadLabel />
                              <Component {...pageProps} />
                            </BannerOrchestrator>
                          </ModalProvider>
                        </HeadsupProvider>
                      </IntlProvider>
                    </LanguageProvider>
                  </StrictMode>
                </ThemeProviderWrapper>
              </LabelProvider>
            </AuthProvider>
          </SWRConfig>
        </StyleSheetManager>
      </CookiesProvider>
    </ErrorBoundary>
  );
}

// This implements the default behavior from styled-components v5
export const shouldForwardProp = (propName: any, target: any) => {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

export default wrapper.withRedux(MyApp);
